import { useState, useEffect } from 'react';
import { ChevronLeft, Eye, Archive, Mail, X, CheckCircle2, Minus, FileDown } from 'lucide-react';

function formatData(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function EstatBadge({ estat }) {
  if (estat === 'firmat') {
    return (
      <span className="inline-flex items-center gap-1 text-green-600 font-medium text-sm">
        <CheckCircle2 size={14} /> Firmat
      </span>
    );
  }
  if (estat === 'sense_firma') {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 font-medium text-sm">
        <X size={14} /> Sense firma
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-gray-400 font-medium text-sm">
      <Minus size={14} /> Pendent
    </span>
  );
}

function ModalPDF({ pdfPath, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white" onClick={e => e.stopPropagation()}>
        <span className="text-sm font-medium truncate">{title}</span>
        <button
          onClick={onClose}
          className="ml-4 flex-shrink-0 rounded-lg border border-white/30 px-3 py-1 text-sm hover:bg-white/10 transition-colors min-h-[36px]"
        >
          Tancar
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-2 pb-2" onClick={e => e.stopPropagation()}>
        <iframe
          src={pdfPath}
          title={title}
          style={{ width: '92vw', height: '90vh', border: 'none', borderRadius: '8px', background: '#fff' }}
        />
      </div>
    </div>
  );
}

function ModalReenviar({ comerc, onClose, onDone }) {
  const [loading, setLoading] = useState(false);

  async function handleSi() {
    setLoading(true);
    try {
      const res = await fetch(`/api/comercos/${comerc.id}/reenviar-email`, { method: 'POST' });
      const data = await res.json();
      onDone(data.ok ? 'ok' : 'error');
    } catch {
      onDone('error');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
        <h3 className="font-bold text-base mb-2" style={{ color: '#444444' }}>Reenviar document</h3>
        <p className="text-sm text-gray-600 mb-6">
          Reenviar el document a <strong>{comerc.email}</strong>?
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleSi}
            disabled={loading}
            className="flex-1 rounded-xl text-white font-semibold text-sm min-h-[48px]"
            style={{ backgroundColor: '#444444' }}
          >
            {loading ? 'Enviant...' : 'Sí'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-xl font-semibold text-sm min-h-[48px] border"
            style={{ color: '#444444', borderColor: '#444444' }}
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg, color, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium"
      style={{ backgroundColor: color }}
    >
      {msg}
    </div>
  );
}

export default function Registre({ onBack }) {
  const [comercos, setComercos] = useState([]);
  const [encuestadors, setEncuestadors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreEnc, setFiltreEnc] = useState('Tots');
  const [sortKey, setSortKey] = useState('nom_comercial');
  const [sortDir, setSortDir] = useState('asc');
  const [pdfModal, setPdfModal] = useState(null); // { path, title }
  const [reenviarModal, setReenviarModal] = useState(null); // comerc object
  const [toast, setToast] = useState(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    Promise.all([
      fetch('/api/comercos').then(r => r.json()),
      fetch('/api/encuestadors').then(r => r.json()),
    ]).then(([c, e]) => {
      setComercos(c);
      setEncuestadors(e);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortIcon({ col }) {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1" style={{ color: '#DF273F' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = comercos
    .filter(c => {
      if (debouncedSearch && !c.nom_comercial.toLowerCase().includes(debouncedSearch.toLowerCase())) return false;
      if (filtreEnc !== 'Tots' && c.nom_enquestador !== filtreEnc) return false;
      return true;
    })
    .sort((a, b) => {
      let va = a[sortKey] ?? '';
      let vb = b[sortKey] ?? '';
      if (sortKey === 'data_visita') { va = va || '0'; vb = vb || '0'; }
      const cmp = String(va).localeCompare(String(vb), 'ca', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  function handleReenviarDone(result) {
    setReenviarModal(null);
    if (result === 'ok') {
      setToast({ msg: 'Correu reenviat correctament', color: '#16a34a' });
    } else {
      setToast({ msg: "Error en l'enviament del correu.", color: '#d97706' });
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center pt-20 text-gray-400">Carregant...</div>
    );
  }

  return (
    <div className="flex-1 flex flex-col" style={{ backgroundColor: '#f5f6fa' }}>
      {/* Línia d'acent vermell sota la capçalera */}
      <div style={{ height: '3px', backgroundColor: '#DF273F', flexShrink: 0 }} />

      {/* Sub-header */}
      <div className="px-4 pt-5 pb-3 max-w-5xl w-full mx-auto">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          {/* Filtres */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              type="search"
              placeholder="Cercar per nom comercial..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm text-gray-700 outline-none min-h-[40px]"
              style={{ border: '1px solid #DF273F', maxWidth: '280px' }}
            />
            <select
              value={filtreEnc}
              onChange={e => setFiltreEnc(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm text-gray-700 outline-none min-h-[40px]"
              style={{ border: '1px solid #DF273F' }}
            >
              <option value="Tots">Tots els encuestadors</option>
              {encuestadors.map(e => (
                <option key={e.id} value={e.nom}>{e.nom}</option>
              ))}
            </select>
          </div>

          {/* Botó informe */}
          <button
            onClick={() => { window.location.href = '/api/comercos/informe'; }}
            className="flex items-center gap-2 rounded-xl text-white font-semibold text-sm px-4 min-h-[40px] flex-shrink-0"
            style={{ backgroundColor: '#444444' }}
          >
            <FileDown size={15} />
            Generar informe
          </button>
        </div>

        {/* Taula */}
        <div>
          {/* Capçalera */}
          <div className="grid gap-2 px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
            style={{ gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr 1fr' }}>
            <button
              className="text-left flex items-center hover:text-gray-800 transition-colors min-h-[36px]"
              onClick={() => toggleSort('nom_comercial')}
            >
              Nom comercial <SortIcon col="nom_comercial" />
            </button>
            <button
              className="text-left flex items-center hover:text-gray-800 transition-colors"
              onClick={() => toggleSort('estat')}
            >
              Estat <SortIcon col="estat" />
            </button>
            <span className="text-left flex items-center">Motiu</span>
            <button
              className="text-left flex items-center hover:text-gray-800 transition-colors"
              onClick={() => toggleSort('data_visita')}
            >
              Data <SortIcon col="data_visita" />
            </button>
            <span className="text-left flex items-center">Accions</span>
          </div>

          {/* Files */}
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-400 text-sm">Cap comerç trobat</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {filtered.map(c => (
                <div
                  key={c.id}
                  className="grid gap-2 px-4 items-center min-h-[44px]"
                  style={{
                    gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr 1fr',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <span className="text-sm font-medium truncate" style={{ color: '#444444' }}>{c.nom_comercial}</span>
                  <span><EstatBadge estat={c.estat} /></span>
                  <span className="text-xs text-gray-400 truncate">{c.motiu_sense_firma || '—'}</span>
                  <span className="text-sm text-gray-400">{formatData(c.data_visita)}</span>
                  <div className="flex items-center gap-2">
                    {c.pdf_path && (
                      <button
                        title="Veure PDF actual"
                        onClick={() => setPdfModal({ path: c.pdf_path, title: c.nom_comercial })}
                        className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    {c.pdf_arxivat_path && (
                      <button
                        title="Veure PDF arxivat"
                        onClick={() => setPdfModal({ path: c.pdf_arxivat_path, title: c.nom_comercial + ' (arxivat)' })}
                        className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                      >
                        <Archive size={16} />
                      </button>
                    )}
                    {c.pdf_path && c.email && (
                      <button
                        title="Reenviar correu"
                        onClick={() => setReenviarModal(c)}
                        className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                      >
                        <Mail size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {filtered.length > 0 && (
          <p className="text-xs text-gray-400 mt-2 text-right">
            {filtered.length} resultats{debouncedSearch ? ` per "${debouncedSearch}"` : ''}
            {filtreEnc !== 'Tots' ? ` · ${filtreEnc}` : ''}
          </p>
        )}
      </div>

      {/* Modals */}
      {pdfModal && (
        <ModalPDF
          pdfPath={pdfModal.path}
          title={pdfModal.title}
          onClose={() => setPdfModal(null)}
        />
      )}

      {reenviarModal && (
        <ModalReenviar
          comerc={reenviarModal}
          onClose={() => setReenviarModal(null)}
          onDone={handleReenviarDone}
        />
      )}

      {toast && (
        <Toast
          msg={toast.msg}
          color={toast.color}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
